import type { Artist } from './artist-types';
import type { TypesenseCarouselTile } from './carousel-documents';
import type { SetlistContext } from './client';
import {
  CONTRIBUTORS_COLLECTION,
  MOTION_GRAPHICS_COLLECTION,
} from './collections';
import type { ContributorDocument } from './contributor-documents';
import { escapeTypesenseContributorName } from './contributor-filters';
import {
  MAX_ROWS_PER_CONTRIBUTOR,
  mapContributorType,
} from './contributor-type-mapping';
import type {
  ContributorAssetType,
  ContributorProfile,
} from './contributor-types';
import { LUTS_FILTER } from './lut-filters';
import { MOTION_GRAPHICS_FILTER } from './motion-graphics-filter';

const ASSET_TYPE_ORDER: ContributorAssetType[] = [
  'music',
  'sfx',
  'stock-video',
  'motion-graphics',
  'luts',
];

const sortAssetTypes = (
  types: Iterable<ContributorAssetType>
): ContributorAssetType[] => {
  const set = new Set(types);
  return ASSET_TYPE_ORDER.filter((type) => set.has(type));
};

// Merge fields across every row that describes this contributor. Per-asset-type
// rows often have sparse metadata (e.g. the video row has no description while
// the motion-graphics row does), so for each profile field we take the first
// row that actually has a value. The seed row (the one whose slug matched the
// URL) still owns id/name/slug so canonicalisation behaves predictably.
const buildArtist = (
  seed: ContributorDocument,
  allDocs: readonly ContributorDocument[]
): Artist => {
  const firstNonEmpty = (
    field: 'image' | 'description' | 'meta_title' | 'meta_description'
  ): string | undefined =>
    allDocs.map((doc) => doc[field]).find((value) => !!value?.trim());

  const image = firstNonEmpty('image');
  const description = firstNonEmpty('description');
  const metaTitle = firstNonEmpty('meta_title');
  const metaDescription = firstNonEmpty('meta_description');

  return {
    id: seed.contributor_id,
    name: seed.name,
    slug: seed.slug,
    ...(image && { avatar_image: { image_aws_url: image } }),
    ...(description && {
      short_description: description,
      full_description: description,
    }),
    ...(metaTitle && { meta_title: metaTitle }),
    ...(metaDescription && { meta_description: metaDescription }),
  };
};

const fetchOneBySlug = async (
  context: SetlistContext,
  slug: string
): Promise<ContributorDocument | null> => {
  try {
    const response = await context.client
      .collections<ContributorDocument>(CONTRIBUTORS_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'name',
          filter_by: `slug:=${slug}`,
          per_page: 1,
        },
        { cacheSearchResultsForSeconds: 60 }
      );

    return (response.hits?.[0]?.document as ContributorDocument) ?? null;
  } catch (error) {
    // A thrown error here means Typesense itself failed (network, 5xx,
    // schema mismatch) — semantically different from "slug doesn't exist",
    // which returns an empty hits array. Capture so an outage surfaces
    // instead of looking like a wave of contributor 404s.
    context.onError(error, { operation: 'fetchOneBySlug', slug });
    return null;
  }
};

// LUTs aren't a distinct contributor_type in contributors.v1 — both Motion
// Graphics and LUTs live in motiongraphics.v2 under the same motion-graphics
// contributor_type, separated only by the `is_lut` flag. Probe that collection
// with an explicit is_lut filter to tell which the contributor actually has,
// so a LUT-only contributor doesn't get an empty MG tab (and vice versa).
const hasMotionGraphicsMatching = async (
  context: SetlistContext,
  name: string,
  filter: string,
  helperTag: string
): Promise<boolean> => {
  const escapedName = escapeTypesenseContributorName(name);
  if (!escapedName) {
    return false;
  }

  try {
    const response = await context.client
      .collections(MOTION_GRAPHICS_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'name',
          filter_by: `${filter} && contributor.name:=\`${escapedName}\``,
          per_page: 1,
          include_fields: 'id',
        },
        { cacheSearchResultsForSeconds: 300 }
      );

    return (response.found ?? 0) > 0;
  } catch (error) {
    context.onError(error, { operation: helperTag, contributorName: name });
    return false;
  }
};

const fetchAllByName = async (
  context: SetlistContext,
  name: string
): Promise<ContributorDocument[]> => {
  const escapedName = escapeTypesenseContributorName(name);
  if (!escapedName) {
    return [];
  }

  try {
    const response = await context.client
      .collections<ContributorDocument>(CONTRIBUTORS_COLLECTION)
      .documents()
      .search(
        {
          q: '*',
          query_by: 'name',
          filter_by: `name:=\`${escapedName}\``,
          per_page: MAX_ROWS_PER_CONTRIBUTOR,
        },
        { cacheSearchResultsForSeconds: 60 }
      );

    const docs = (response.hits ?? [])
      .map((hit) => hit.document)
      .filter((doc): doc is ContributorDocument => !!doc);

    // Pin the order in JS so `buildArtist`'s first-non-empty merge picks
    // the same row every time. Without this, Typesense's default ranking
    // can return per-asset-type rows in different orders across requests,
    // which would make the displayed image / description / meta
    // non-deterministic when rows disagree. We sort post-fetch rather than
    // via `sort_by` because the `contributors.v1` schema doesn't declare
    // `updated_at` / `contributor_type` as sortable fields, so the typesense
    // request would 400 and the try/catch would silently drop every row
    // (turning a multi-asset contributor into a music-only one in the UI).
    // Most-recently-reindexed row wins for metadata; ties break on type
    // alphabetically.
    return docs.sort((a, b) => {
      const updatedDelta = (b.updated_at ?? 0) - (a.updated_at ?? 0);
      if (updatedDelta !== 0) {
        return updatedDelta;
      }
      return a.contributor_type.localeCompare(b.contributor_type);
    });
  } catch (error) {
    context.onError(error, {
      operation: 'fetchAllByName',
      contributorName: name,
    });
    return [];
  }
};

// Per the backend contract, contributors with multiple asset types are grouped
// by `name` in `contributors.v1` (one row per asset type, all sharing a name,
// potentially with different slugs). So we look up the seed row by slug to
// identify the contributor, then re-query by name to gather every asset-type
// row they have, regardless of slug variants.
export const getContributor = async (
  context: SetlistContext,
  slug: string
): Promise<ContributorProfile | null> => {
  // Sequential by necessity: fetchAllByName needs the canonical contributor
  // name, which only the URL-slug seed row carries reliably (per-asset-type
  // rows can vary in slug). The hasLutAssets probe below is conditional, so
  // the three round-trips collapse to two for non-MG contributors.
  const seed = await fetchOneBySlug(context, slug);

  if (!seed) {
    return null;
  }

  const allDocs = await fetchAllByName(context, seed.name);
  const docs = allDocs.length > 0 ? allDocs : [seed];

  const detectedTypes = new Set(
    docs
      .map((doc) => mapContributorType(doc.contributor_type))
      .filter((type): type is ContributorAssetType => type !== null)
  );

  // A motion-graphics contributor_type row exists whenever the contributor has
  // any motiongraphics.v2 asset — including LUT-only contributors, since LUTs
  // share that contributor_type. The MG and LUT tab content each scope to one
  // side of the is_lut flag, so probe both and only surface the tabs backed by
  // real assets. Run in parallel: two independent motiongraphics.v2 round-trips.
  if (detectedTypes.has('motion-graphics')) {
    const [hasMotionGraphics, hasLuts] = await Promise.all([
      hasMotionGraphicsMatching(
        context,
        seed.name,
        MOTION_GRAPHICS_FILTER,
        'hasMotionGraphicsAssets'
      ),
      hasMotionGraphicsMatching(
        context,
        seed.name,
        LUTS_FILTER,
        'hasLutAssets'
      ),
    ]);

    if (hasLuts) {
      detectedTypes.add('luts');
    }

    // Only drop the MG tab once we've positively confirmed LUTs but no non-LUT
    // motion graphics. If both probes come back empty (e.g. a Typesense
    // outage), leave the detected type in place rather than hiding a tab that
    // should exist.
    if (hasLuts && !hasMotionGraphics) {
      detectedTypes.delete('motion-graphics');
    }
  }

  // contributors.v1 has one row per asset type, each carrying its own
  // featured_tiles. Key the tiles by asset type so the page can render only
  // the bucket that matches the active tab. Multiple raw rows mapping to the
  // same asset type (rare, but possible mid-migration) get merged, and we
  // dedupe by slug so the page never emits duplicate React keys when a tile
  // appears on more than one source row.
  const featuredTiles: Partial<
    Record<ContributorAssetType, TypesenseCarouselTile[]>
  > = {};
  for (const doc of docs) {
    const assetType = mapContributorType(doc.contributor_type);
    if (!assetType) {
      continue;
    }
    const tiles = doc.featured_tiles;
    if (!Array.isArray(tiles) || tiles.length === 0) {
      continue;
    }
    const merged = [...(featuredTiles[assetType] ?? []), ...tiles];
    const seenSlugs = new Set<string>();
    featuredTiles[assetType] = merged.filter((tile) => {
      if (!tile.slug || seenSlugs.has(tile.slug)) {
        return false;
      }
      seenSlugs.add(tile.slug);
      return true;
    });
  }
  const hasFeaturedTiles = Object.values(featuredTiles).some(
    (tiles) => tiles && tiles.length > 0
  );

  return {
    ...buildArtist(seed, docs),
    assetTypes: sortAssetTypes(detectedTypes),
    ...(hasFeaturedTiles && { featuredTiles }),
  };
};
